import type Store from '@ember-data/store';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import FileDisplayer from 'parog-web/components/file-displayer';
import type FileAttachment from 'parog-web/models/file-attachment';

interface Signature {
  Args: {
    fileAttachment: FileAttachment;
    alternativeText: string;
    shouldDisplayDownloadLink?: boolean;
    shouldDisplayTextWithDownloadIcon?: boolean;
    downloadText?: string;
  };
}

export default class FileAttachmentDisplayer extends Component<Signature> {
  @service declare store: Store;

  @tracked declare documentContent: string | undefined;

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    if (!this.args.fileAttachment.url) {
      void this.retrieveDocumentContent.perform();
    }
  }

  /**
   * Async loading of the file attachment content
   * into a variable, which will be used :
   * - to display an iFrame if the user is not on a mobile
   * - to allow user to download the file given its content and mimeType,
   *   because iFrame aren't supported on every mobile and browsers
   */
  retrieveDocumentContent = task(async () => {
    const { datadogRum } = await import('@datadog/browser-rum');
    datadogRum.startDurationVital('retrieve-document-content');
    const fileContent = await this.args.fileAttachment.fileContent;
    this.documentContent = fileContent.data;
    datadogRum.stopDurationVital('retrieve-document-content');
  });

  <template>
    <FileDisplayer
      @fileName={{@fileAttachment.filename}}
      @mimeType={{@fileAttachment.mimeType}}
      @alternativeText={{@alternativeText}}
      @documentContent={{this.documentContent}}
      @isRunning={{this.retrieveDocumentContent.isRunning}}
      @shouldDisplayDownloadLink={{@shouldDisplayDownloadLink}}
      @shouldDisplayTextWithDownloadIcon={{@shouldDisplayTextWithDownloadIcon}}
      @downloadText={{@downloadText}}
      @documentUrl={{@fileAttachment.url}}
    />
  </template>
}
